'use client'

import { TrackingDataLayer } from '@/components/extension/tracking-data-layer'
import { Button } from '@/components/ui/button'
import { useBotNoticeErrorMutation } from '@/hooks/react-query/bot/use-bot-notice-error.mutation'
import { ETrackingEvent } from '@/lib/gtm/type'
import { useTranslations } from 'next-intl'
import { useEffect, useState, useSyncExternalStore } from 'react'

const publicEnv = process.env.NEXT_PUBLIC_ENV
const AUTO_RELOAD_KEY_PREFIX = 'collection-empty-auto-reload:'
const PENDING_LOCALE_SWITCH_KEY = 'pending-locale-switch'

/**
 * true nếu render này xảy ra ngay sau khi user vừa đổi ngôn ngữ (soft
 * navigation, xem language-switcher/index.tsx). Đọc xong xoá ngay để chỉ áp
 * dụng đúng 1 lần, không lẫn sang các lần đổi filter sau đó trên cùng trang.
 */
const consumePendingLocaleSwitch = (): boolean => {
  try {
    const isPending = sessionStorage.getItem(PENDING_LOCALE_SWITCH_KEY) === '1'
    if (isPending) sessionStorage.removeItem(PENDING_LOCALE_SWITCH_KEY)
    return isPending
  } catch {
    return false
  }
}

/**
 * Reload trang 1 lần duy nhất cho mỗi URL khi danh sách sản phẩm rỗng.
 * @returns true nếu đã trigger reload
 */
const tryAutoReloadOnce = (): boolean => {
  try {
    const key = `${AUTO_RELOAD_KEY_PREFIX}${window.location.href}`
    if (sessionStorage.getItem(key)) return false

    sessionStorage.setItem(key, '1')
    window.location.reload()
    return true
  } catch {
    // sessionStorage không khả dụng → không reload để tránh vòng lặp vô hạn
    return false
  }
}

const noopSubscribe = () => () => {}

/**
 * Trả về true nếu component này được hydrate từ HTML server (tức lần đầu vào trang),
 * false nếu mount mới ở client (đổi filter, điều hướng SPA...).
 * Khi hydrate, lần render đầu React dùng getServerSnapshot (true); mount ở client dùng getSnapshot (false).
 */
const useIsHydratedFromServer = (): boolean => {
  const snapshot = useSyncExternalStore(
    noopSubscribe,
    () => false,
    () => true
  )
  // Giữ lại giá trị của lần render đầu tiên, các lần re-render sau snapshot sẽ đổi thành false
  const [isFirstRenderFromServer] = useState(snapshot)
  return isFirstRenderFromServer
}

export const CollectionEmptyProductList = () => {
  const { mutateAsync: sendErrorNotice } = useBotNoticeErrorMutation()
  const t = useTranslations('collection_page.message')
  const isInitialPageLoad = useIsHydratedFromServer()

  useEffect(() => {
    // Có thể là lỗi API tạm thời: chỉ khi vào trang lần đầu (hard load) hoặc
    // ngay sau khi đổi ngôn ngữ (soft navigation, BE cache theo locale mới có
    // thể chưa warm) mới tự reload (1 lần/URL). Đổi filter ra rỗng là kết quả
    // hợp lệ nên không reload.
    if ((isInitialPageLoad || consumePendingLocaleSwitch()) && tryAutoReloadOnce())
      return

    handleSendErrorNotice()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSendErrorNotice = async () => {
    if (publicEnv !== 'local' && typeof window !== 'undefined') {
      sendErrorNotice(
        `ENV:${publicEnv?.toUpperCase()} | Không tìm thấy sản phẩm nào | ${window?.location?.href}`
      )
    }
  }

  return (
    <>
      <div className='col-span-full flex flex-col items-center'>
        <p className='no-product-found text-center text-sm text-neutral-900/60'>
          {t('no_product_found')}
        </p>
        <Button
          variant='outline-neutral'
          size='small'
          className='mt-4'
          onClick={() => window.location.reload()}
        >
          {t('reload_page')}
        </Button>
      </div>
      <TrackingDataLayer
        data={{
          event: ETrackingEvent.ProductNotFound,
          url: typeof window !== 'undefined' ? window?.location?.href : '',
        }}
      />
    </>
  )
}
